/**
 * TypeScript interfaces for Canary Status functionality
 * Aligned with X-Planner API response DTOs
 */

// API Response Types (matching planner backend structure)

export interface CanaryRunResponse {
  run: CanaryRunDTO;
  phases: CanaryPhaseDTO[];
}

export interface CanaryRunDTO {
  id: number;
  variableName: string;
  diffId: string;
  preCanaryRollout: any;
  intendedRollout: any;
  targetRolloutIds: string;
  canaryConfigSnapshot: any;
  status: 'active' | 'completed' | 'rolled_back' | 'cancelled';
  currentPhaseIndex: number;
  startedAt: string;
  completedAt?: string;
  finalDecision?: string;
  finalDecisionBy?: string;
  finalDecisionReason?: string;
  rollbackDiffId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CanaryPhaseDTO {
  id: number;
  canaryRunId: number;
  phaseIndex: number;
  targetPercentage: number;
  durationMins: number;
  status:
    | 'yet_to_start'
    | 'in_progress'
    | 'awaiting_decision'
    | 'threshold_breached'
    | 'completed'
    | 'rolled_back'
    | 'skipped';
  startedAt?: string;
  expectedEndAt?: string;
  actualEndAt?: string;
  metricValuesAtStart?: Record<string, number> | null;
  metricValuesAtEnd?: Record<string, number> | null;
  metricCheckLog?: MetricCheckEntry[];
  lastCheckedAt?: string;
  thresholdBreached: boolean;
  breachCount: number;
  totalChecks: number;
  decision?: string;
  decisionBy?: string;
  decisionAt?: string;
  decisionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MetricCheckEntry {
  checkedAt: string;
  metrics?: Record<string, number>;
  breached: boolean;
  breachedMetrics?: string[];
  // error is set when the Datadog query failed for this check.
  // A non-empty error means metrics is absent — this is a read failure,
  // not a zero value. Distinguish in UI accordingly.
  error?: string;
}

export interface CanaryRunListResponse {
  runs: CanaryRunDTO[];
  total: number;
  limit: number;
  offset: number;
}

// UI Component Types

export interface CanaryMetricConfig {
  displayName: string;
  query?: string;
  min?: number;
  max?: number;
  type?: string;
  monitorUrl?: string;
  monitorURL?: string;
  monitorId?: number;
}

export interface CanaryMetric {
  displayName: string;
  name: string;
  value: number | string;
  unit?: string;
  status?: 'healthy' | 'warning' | 'critical';
}

export interface CanaryPhase {
  id: number;
  phaseIndex: number;
  name: string;
  status:
    | 'yet_to_start'
    | 'in_progress'
    | 'awaiting_decision'
    | 'threshold_breached'
    | 'completed'
    | 'rolled_back'
    | 'skipped';
  targetPercentage: number;
  durationMins: number;
  timeRemaining?: number;
  startTime?: string;
  endTime?: string;
  expectedEndAt?: string;
  thresholdBreached: boolean;
  breachCount: number;
  totalChecks: number;
  decision?: string;
  decisionBy?: string;
  decisionAt?: string;
  decisionReason?: string;
  metricValuesAtStart?: Record<string, number> | null;
  metricValuesAtEnd?: Record<string, number> | null;
  metricCheckLog?: MetricCheckEntry[];
}

export interface CanaryRollout {
  id: number;
  variableName: string;
  diffId?: string;
  status: 'active' | 'completed' | 'rolled_back' | 'cancelled';
  triggeredBy: string;
  startTime: string;
  currentPhaseIndex: number;
  phases: CanaryPhase[];
  finalDecision?: string;
  finalDecisionReason?: string;
  metricsConfig?: CanaryMetricConfig[];
}

export interface CanaryStatusProps {
  canaryRollout: CanaryRollout | null;
  isLoading?: boolean;
  error?: string;
  onAdvance?: (runId: number, reason: string) => void;
  onHold?: (runId: number, mins: number) => void;
  onRollback?: (runId: number, reason: string) => void;
  onPromote?: (runId: number, reason: string) => void;
  onOverride?: (runId: number, reason: string) => void;
  onRefresh?: () => void;
  onModalStateChange?: (isOpen: boolean) => void;
  showCanaryActiveWarning?: boolean;
}

// Transformer: API response -> UI model

export function transformCanaryResponse(
  response: CanaryRunResponse,
): CanaryRollout {
  const { run, phases } = response;

  const metricsConfig: CanaryMetricConfig[] | undefined =
    run.canaryConfigSnapshot?.metrics;

  return {
    id: run.id,
    variableName: run.variableName,
    diffId: run.diffId,
    status: run.status,
    triggeredBy: run.createdBy,
    startTime: run.startedAt,
    currentPhaseIndex: run.currentPhaseIndex,
    finalDecision: run.finalDecision,
    finalDecisionReason: run.finalDecisionReason,
    phases: phases.map((p) => transformPhase(p)),
    metricsConfig,
  };
}

function transformPhase(phase: CanaryPhaseDTO): CanaryPhase {
  const timeRemaining = calculateTimeRemaining(
    phase.expectedEndAt,
    phase.startedAt,
    phase.durationMins,
    phase.status,
  );

  return {
    id: phase.id,
    phaseIndex: phase.phaseIndex,
    name: `Phase ${phase.phaseIndex + 1}`,
    status: phase.status,
    targetPercentage: phase.targetPercentage,
    durationMins: phase.durationMins,
    timeRemaining,
    startTime: phase.startedAt,
    endTime: phase.actualEndAt,
    expectedEndAt: phase.expectedEndAt,
    thresholdBreached: phase.thresholdBreached,
    breachCount: phase.breachCount,
    totalChecks: phase.totalChecks,
    decision: phase.decision,
    decisionBy: phase.decisionBy,
    decisionAt: phase.decisionAt,
    decisionReason: phase.decisionReason,
    metricValuesAtStart: phase.metricValuesAtStart,
    metricValuesAtEnd: phase.metricValuesAtEnd,
    metricCheckLog: phase.metricCheckLog,
  };
}

/**
 * Uses expectedEndAt when available (accurate after a Hold extension).
 * Falls back to startedAt + durationMins for phases that haven't been held.
 */
function calculateTimeRemaining(
  expectedEndAt?: string,
  startedAt?: string,
  durationMins?: number,
  status?: string,
): number {
  if (
    status === 'completed' ||
    status === 'rolled_back' ||
    status === 'skipped' ||
    status === 'awaiting_decision' ||
    status === 'threshold_breached'
  ) {
    return 0;
  }

  if (status === 'yet_to_start') {
    return durationMins ?? 0;
  }

  // Prefer expectedEndAt — reflects hold extensions
  if (expectedEndAt) {
    const end = new Date(expectedEndAt).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((end - now) / 1000 / 60));
  }

  // Fallback: estimate from start + duration
  if (startedAt && durationMins) {
    const start = new Date(startedAt).getTime();
    const elapsed = Math.floor((Date.now() - start) / 1000 / 60);
    return Math.max(0, durationMins - elapsed);
  }

  return 0;
}
